import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Settings,
  ArrowRight,
  Building,
  UserCheck,
  Clipboard,
  TrendingUp,
  Shield,
  Clock,
  Target
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const SubcontractingLanding = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const subcontractingCards = [
    {
      id: 'subcontractor-management',
      title: 'Subcontractor Management',
      description: 'Manage vendor profiles & performance tracking',
      icon: <Users size={24} />,
      path: '/subcontracting/management',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Vendor profiles', 'Certification tracking', 'Performance metrics', 'Compliance monitoring']
    },
    {
      id: 'work-orders',
      title: 'Work Orders',
      description: 'Create, assign & track work orders',
      icon: <Clipboard size={24} />,
      path: '/subcontracting/work-orders',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Work order creation', 'Assignment tracking', 'Progress monitoring', 'Completion status']
    },
    {
      id: 'vendor-onboarding',
      title: 'Vendor Onboarding',
      description: 'Streamlined vendor registration & approval',
      icon: <UserCheck size={24} />,
      path: '/subcontracting/onboarding',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Registration forms', 'Document collection', 'Approval workflow', 'Compliance checks']
    },
    {
      id: 'performance-tracking',
      title: 'Performance Tracking',
      description: 'Monitor performance & quality metrics',
      icon: <TrendingUp size={24} />,
      path: '/subcontracting/performance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Quality metrics', 'Performance scores', 'Rating system', 'Feedback tracking']
    },
    {
      id: 'compliance-management',
      title: 'Compliance Management',
      description: 'Ensure compliance with regulations & standards',
      icon: <Shield size={24} />,
      path: '/subcontracting/compliance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Regulatory compliance', 'Safety standards', 'Insurance tracking', 'Audit management']
    },
    {
      id: 'billing-payments',
      title: 'Billing & Payments',
      description: 'Manage billing, invoicing & payment processing',
      icon: <DollarSign size={24} />,
      path: '/subcontracting/billing',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Invoice processing', 'Payment tracking', 'Budget management', 'Cost analysis']
    },
    {
      id: 'resource-allocation',
      title: 'Resource Allocation',
      description: 'Optimize resource allocation & capacity planning',
      icon: <Target size={24} />,
      path: '/subcontracting/resources',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Capacity planning', 'Resource optimization', 'Scheduling', 'Availability tracking']
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      description: 'Generate comprehensive reports & analytics',
      icon: <FileText size={24} />,
      path: '/subcontracting/reports',
      iconBg: '#D6EFFF',
      iconLine: '#2D7DD2',
      features: ['Performance reports', 'Cost analysis', 'Compliance reports', 'Trend analysis']
    }
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  // Filter subcontracting cards based on search query
  const filteredCards = subcontractingCards.filter(card =>
    card.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Breadcrumb and Search Bar */}
      <div
        className="d-flex justify-content-between align-items-center mb-5"
        style={{ gap: '16px', flexWrap: 'wrap' }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <PageBreadcrumb />
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <input
            type="text"
            placeholder="Search Modules..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 16px',
              borderRadius: '10px',
              border: '1px solid #D1D5DB',
              width: '300px',
              fontSize: '14px',
              outline: 'none',
              transition: '0.2s',
            }}
          />
        </div>
      </div>

      {/* Page Header */}
      <PageHeader
        title="Subcontracting Management"
        description="Comprehensive subcontractor management, work orders, and vendor onboarding system"
      />

      <div className="page-container">
        {/* Subcontracting Management Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {filteredCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%'
                }}
              />
              <div style={{ padding: '24px' }}>
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: card.iconLine }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>
                <p style={{
                  fontSize: '13px',
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {card.description}
                </p>
                <div>
                  <div style={{
                    fontSize: '11px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SubcontractingLanding;